import React from 'react';
import PropTypes from 'prop-types';

export default class Preload extends React.Component {
  static propTypes = {
    // Rendered on success
    children: PropTypes.element.isRequired,

    // Rendered during load
    loadingIndicator: PropTypes.node.isRequired,

    // Media url to be preloaded
    media: PropTypes.string,

    // Error callback. Is passed the error
    onError: PropTypes.func,

    // Success callback
    onLoad: PropTypes.func,

    // Type of media that is preloaded.
    type: PropTypes.string,

    // To render the children or not
    ready: PropTypes.bool,
  };

  static defaultProps = {
    media: null,
    loadingIndicator: null,
    ready: false,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.handleError = this.handleError.bind(this);
    this.mounted = false; // The mounted flag is used to prevent unnecessary error/success calls
  }

  /**
   * Fires the call to preload the media if it isn't ready yet.
   */
  componentDidMount() {
    this.mounted = true;
    if (!this.props.ready) {
      this.loadMedia(this.props.media);
    }
  }

  /**
   * Preloads media files. For now limited to images
   * @param  {String} mediaFile The uri to the media file.
   */
  loadMedia(mediaFile) {
    if (!mediaFile) {
      return;
    }

    switch (this.props.type) {
      case 'image': {
        const media = new Image();
        media.onload = this.handleSuccess;
        media.onerror = this.handleError;
        media.src = mediaFile;
        break;
      }
      case 'audio': {
        this.handleSuccess();
        break;
      }
    }
  }

  /**
   * Calls loadMedia if the media file changes.
   * @param  {Object} nextProps The next props
   * @return {[type]}           The current props.
   */
  componentDidUpdate(prevProps) {
    if (this.props.media && !prevProps.ready) {
      this.loadMedia(this.props.media);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  /**
   * Calls the onload prop (if set) if the component is mounted but not ready.
   */
  handleSuccess() {
    if (this.props.ready || !this.mounted) {
      return;
    }

    if (this.props.onLoad) {
      this.props.onLoad();
    }
  }

  /**
   * Calls the onError prop (if set) if the component is mounted
   * @param  {Error} err  The Error object
   */
  handleError(err) {
    if (!this.mounted) {
      return;
    }

    if (this.props.onError) {
      this.props.onError(err);
    }
  }

  /**
   * Renders either the loadingIndicator or the children (if ready)
   * @return {HtmlElement} Components children or loading indicator
   */
  render() {
    if (this.props.ready) {
      return this.props.children;
    }
    return this.props.loadingIndicator;
  }
}
